/**
 * Shared timeline FX math — must stay in sync with backend timeline_fx.py
 *
 * v2: extensible registries.
 *  - TRANSITIONS: per-overlay in/out transitions (fade, slide-*, zoom)
 *  - GLOBAL_FX_CONTROLS: whole-video effects rendered from a single config
 *
 * Adding a new global FX = one entry in GLOBAL_FX_CONTROLS + one branch in
 * computeGlobalFilterCss (preview) + one branch in timeline_fx.py
 * color_fx_filters (export). Adding a transition = one entry in TRANSITIONS
 * + a case in computeOverlayPreviewStyle + matching expr in timeline_fx.py.
 */

export function clamp(n, min, max) {
  const v = Number.isFinite(+n) ? +n : 0;
  return Math.min(Math.max(v, min), max);
}

/* ════════════════════════════════════════════════════════════════
   KEN BURNS (unchanged math)
   ════════════════════════════════════════════════════════════════ */

export function kenProgress(mode, localT, segLen, speed = 8) {
  const m = (mode || "auto").toLowerCase();
  const len = Math.max(segLen, 1e-6);
  const t = Math.max(0, localT);
  if (m === "in") return Math.min(1, t / len);
  if (m === "out") return 1 - Math.min(1, t / len);
  const phase = (t / Math.max(0.001, speed)) * Math.PI * 2;
  return 0.5 * (1 - Math.cos(phase));
}

export function computeKenScale(overlay, currentTime) {
  const ken = overlay?.ken;
  if (!ken?.enabled) return 1;
  const segLen = Math.max(0.001, (overlay.end ?? 0) - (overlay.start ?? 0));
  const localT = Math.max(0, currentTime - (overlay.start ?? 0));
  const strength = clamp(ken.strength ?? 0.2, 0, 0.5);
  const speed = Math.max(0.5, ken.speed ?? 8);
  return 1 + strength * kenProgress(ken.mode || "auto", localT, segLen, speed);
}

/* ════════════════════════════════════════════════════════════════
   TRANSITIONS (per overlay, in + out)
   ════════════════════════════════════════════════════════════════ */

/** Zoom-settle transition scale amount — must match ZOOM_TRANS in timeline_fx.py */
export const ZOOM_TRANS_SCALE = 0.35;

export const TRANSITIONS = [
  { id: "none", label: "None" },
  { id: "fade", label: "Fade" },
  { id: "slide-left", label: "Slide · from left" },
  { id: "slide-right", label: "Slide · from right" },
  { id: "slide-up", label: "Slide · from bottom" },
  { id: "slide-down", label: "Slide · from top" },
  // Zoom is baked into the backend pre-render pass (like Ken Burns), which
  // only exists for image overlays — video overlays downgrade to fade so
  // preview and export never disagree.
  { id: "zoom", label: "Zoom (images)", imagesOnly: true },
];

export function defaultTransition() {
  return { type: "none", duration: 0.5 };
}

/**
 * Normalize a transition config; downgrades images-only types for video
 * overlays. Mirrors timeline_fx.parse_transition.
 */
export function normalizeTransition(t, overlayType) {
  let type = t?.type && TRANSITIONS.some((x) => x.id === t.type) ? t.type : "none";
  if (type === "zoom" && overlayType === "video") type = "fade";
  const duration = type === "none" ? 0 : clamp(t?.duration ?? 0.5, 0.05, 5);
  return { type, duration };
}

/**
 * Which transition (if any) is active at the playhead, and its progress.
 * p goes 0→1 entering, 1→0 approaching the end. p=1 means fully shown.
 */
export function transitionState(overlay, currentTime) {
  const start = Number(overlay?.start) || 0;
  const end = Number(overlay?.end) || 0;
  const tin = normalizeTransition(overlay?.transIn, overlay?.type);
  const tout = normalizeTransition(overlay?.transOut, overlay?.type);

  if (tin.type !== "none") {
    const lt = currentTime - start;
    if (lt >= 0 && lt < tin.duration) {
      return { type: tin.type, mode: "in", p: clamp(lt / tin.duration, 0, 1) };
    }
  }
  if (tout.type !== "none") {
    const rt = end - currentTime;
    if (rt >= 0 && rt < tout.duration) {
      return { type: tout.type, mode: "out", p: clamp(rt / tout.duration, 0, 1) };
    }
  }
  return { type: null, mode: null, p: 1 };
}

/* ════════════════════════════════════════════════════════════════
   OVERLAY FRAMING (fit / focus) + BACKGROUND FILL
   ════════════════════════════════════════════════════════════════ */

export const OVERLAY_FIT_MODES = [
  { id: "contain", label: "Fit (letterbox)" },
  { id: "cover", label: "Fill (crop)" },
];

export const OVERLAY_BG_TYPES = [
  { id: "none", label: "None (transparent)" },
  { id: "blur", label: "Blurred image" },
  { id: "color", label: "Solid color" },
  { id: "gradient", label: "Gradient" },
];

export function defaultOverlayBg() {
  return { type: "none", blur: 20, color: "#000000", color2: "#7c3aed", angle: 180 };
}

/* ════════════════════════════════════════════════════════════════
   OVERLAY PREVIEW STYLES
   Split to mirror the export graph:
   - GROUP: whole framed composite → transition slide + fade/zoom opacity
            + the overlay's base opacity (colorchannelmixer on export)
   - MEDIA: the image/video itself → fit/focus + Ken Burns + zoom-settle
            scale (backend zooms the foreground only; bg stays static)
   - BG:    optional fill layer behind the media (letterboxed fits only)
   ════════════════════════════════════════════════════════════════ */

export function computeOverlayGroupStyle(overlay, currentTime = null) {
  let opacity = clamp(overlay?.opacity ?? 1, 0, 1);
  let tx = 0; // percent of frame width
  let ty = 0;

  if (currentTime != null) {
    const { type, p } = transitionState(overlay, currentTime);
    if (type && p < 1) {
      switch (type) {
        case "fade":
        case "zoom":
          opacity *= p;
          break;
        case "slide-left":
          tx = -(1 - p) * 100;
          break;
        case "slide-right":
          tx = (1 - p) * 100;
          break;
        case "slide-up":
          ty = (1 - p) * 100;
          break;
        case "slide-down":
          ty = -(1 - p) * 100;
          break;
        default:
          break;
      }
    }
  }

  return { opacity, transform: `translate(${tx}%, ${ty}%)` };
}

export function computeOverlayMediaStyle(overlay, kenScale = 1, currentTime = null) {
  const userScale =
    overlay?.scale != null && overlay.scale !== "" ? clamp(overlay.scale, 0.05, 4) : 1;

  let extraScale = 1;
  if (currentTime != null) {
    const { type, p } = transitionState(overlay, currentTime);
    if (type === "zoom" && p < 1) extraScale = 1 + ZOOM_TRANS_SCALE * (1 - p);
  }

  const fit = overlay?.fit === "cover" ? "cover" : "contain";
  const fx = clamp(overlay?.focus?.x ?? 0.5, 0, 1) * 100;
  const fy = clamp(overlay?.focus?.y ?? 0.5, 0, 1) * 100;

  return {
    objectFit: fit,
    objectPosition: `${fx}% ${fy}%`,
    transform: `scale(${kenScale * userScale * extraScale})`,
    transformOrigin: "center center",
  };
}

/**
 * Background layer behind a letterboxed overlay. Returns null when no bg
 * applies (cover fit has no letterbox; video overlays keep bg off — the
 * backend enforces the same rule).
 */
export function computeOverlayBgStyle(overlay) {
  const bg = overlay?.bg;
  if (!bg || bg.type === "none") return null;
  if (overlay?.fit === "cover" || overlay?.type === "video") return null;

  if (bg.type === "blur") {
    const fx = clamp(overlay?.focus?.x ?? 0.5, 0, 1) * 100;
    const fy = clamp(overlay?.focus?.y ?? 0.5, 0, 1) * 100;
    return {
      kind: "image",
      style: {
        objectFit: "cover",
        objectPosition: `${fx}% ${fy}%`,
        filter: `blur(${clamp(bg.blur ?? 20, 1, 100)}px)`,
        transform: "scale(1.12)", // hide blur edge halo
      },
    };
  }
  if (bg.type === "color") {
    return { kind: "div", style: { background: bg.color || "#000000" } };
  }
  // gradient
  const angle = clamp(bg.angle ?? 180, 0, 360);
  return {
    kind: "div",
    style: {
      background: `linear-gradient(${angle}deg, ${bg.color || "#000000"}, ${bg.color2 || "#7c3aed"})`,
    },
  };
}

/* ════════════════════════════════════════════════════════════════
   GLOBAL FX  (whole-video, preview ↔ export)
   ════════════════════════════════════════════════════════════════ */

/** Slider-native units. Converted to normalized values in buildProcessMetadata. */
export function defaultGlobalFx() {
  return {
    brightness: 0, //  -100..100  (%)
    contrast: 0, //    -100..100  (%)
    saturation: 0, //  -100..100  (%)
    hue: 0, //         -180..180  (deg)
    blur: 0, //        0..20      (px)
    grayscale: 0, //   0..100     (%)
    sepia: 0, //       0..100     (%)
    vignette: 0, //    0..100     (%)
    noise: 0, //       0..100     (%)
    fadeIn: 0, //      0..5       (s)
    fadeOut: 0, //     0..5       (s)
  };
}

/** Drives the FX tab UI — add an entry here to get a slider for free. */
export const GLOBAL_FX_CONTROLS = [
  { key: "brightness", label: "Brightness", min: -100, max: 100, step: 1, unit: "%" },
  { key: "contrast", label: "Contrast", min: -100, max: 100, step: 1, unit: "%" },
  { key: "saturation", label: "Saturation", min: -100, max: 100, step: 1, unit: "%" },
  { key: "hue", label: "Hue", min: -180, max: 180, step: 1, unit: "°" },
  { key: "blur", label: "Blur", min: 0, max: 20, step: 1, unit: "px" },
  { key: "grayscale", label: "B & W", min: 0, max: 100, step: 1, unit: "%" },
  { key: "sepia", label: "Sepia", min: 0, max: 100, step: 1, unit: "%" },
  { key: "vignette", label: "Vignette", min: 0, max: 100, step: 1, unit: "%" },
  { key: "noise", label: "Noise", min: 0, max: 100, step: 1, unit: "%" },
  { key: "fadeIn", label: "Fade In", min: 0, max: 5, step: 0.1, unit: "s" },
  { key: "fadeOut", label: "Fade Out", min: 0, max: 5, step: 0.1, unit: "s" },
];

export function hasAnyGlobalFx(fx) {
  if (!fx) return false;
  return GLOBAL_FX_CONTROLS.some((c) => Math.abs(Number(fx[c.key]) || 0) > 1e-9);
}

/**
 * CSS filter string for the composited preview (video + overlays).
 * Applied AFTER overlay compositing — same order as the export graph.
 * Vignette / noise / fades are separate layers, not CSS filters.
 */
export function computeGlobalFilterCss(fx) {
  if (!fx) return "none";
  const parts = [];
  const b = clamp(fx.brightness, -100, 100);
  const c = clamp(fx.contrast, -100, 100);
  const s = clamp(fx.saturation, -100, 100);
  const h = clamp(fx.hue, -180, 180);
  const bl = clamp(fx.blur, 0, 50);
  const g = clamp(fx.grayscale, 0, 100);
  const se = clamp(fx.sepia, 0, 100);
  if (b) parts.push(`brightness(${(1 + b / 100).toFixed(3)})`);
  if (c) parts.push(`contrast(${(1 + c / 100).toFixed(3)})`);
  if (s) parts.push(`saturate(${Math.max(0, 1 + s / 100).toFixed(3)})`);
  if (h) parts.push(`hue-rotate(${h}deg)`);
  if (g) parts.push(`grayscale(${(g / 100).toFixed(3)})`);
  if (se) parts.push(`sepia(${(se / 100).toFixed(3)})`);
  if (bl) parts.push(`blur(${bl}px)`);
  return parts.length ? parts.join(" ") : "none";
}

export function computeVignetteCss(strength) {
  const s = clamp(strength, 0, 1);
  if (s <= 0) return "transparent";
  const alpha = (0.99 * s).toFixed(3);
  return `radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 40%, rgba(0,0,0,${alpha}) 100%)`;
}

/** Opacity of the black fade layer over the preview. Matches ffmpeg fade in/out. */
export function computeFadeOpacity(currentTime, duration, fadeIn, fadeOut) {
  const t = Math.max(0, currentTime);
  let o = 0;
  const fi = clamp(fadeIn, 0, 10);
  const fo = clamp(fadeOut, 0, 10);
  if (fi > 0 && t < fi) o = Math.max(o, 1 - t / fi);
  if (fo > 0 && duration > 0 && t > duration - fo) {
    o = Math.max(o, (t - (duration - fo)) / fo);
  }
  return clamp(o, 0, 1);
}

/* ════════════════════════════════════════════════════════════════
   PLAYHEAD / OVERLAY QUERIES (unchanged)
   ════════════════════════════════════════════════════════════════ */

const TIME_EPS = 0.035;

export function isTimeInOverlay(currentTime, overlay) {
  if (!overlay) return false;
  const start = Number(overlay.start) || 0;
  const end = Number(overlay.end) || 0;
  if (end <= start) return false;
  return currentTime >= start - TIME_EPS && currentTime <= end + TIME_EPS;
}

export function overlaysAtPlayhead(currentTime, overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays.filter((ov) => isTimeInOverlay(currentTime, ov));
}

export function resolveOverlaySrc(url, baseUrl = "") {
  const raw = (url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  try {
    return new URL(raw, baseUrl || window.location.origin).toString();
  } catch {
    return raw;
  }
}

export function defaultOverlay(start, end) {
  return {
    url: "",
    type: "image",
    start,
    end,
    opacity: 1,
    scale: null,
    fit: "contain",
    focus: { x: 0.5, y: 0.5 },
    bg: defaultOverlayBg(),
    ken: { enabled: true, mode: "auto", strength: 0.2, speed: 8 },
    transIn: defaultTransition(),
    transOut: defaultTransition(),
  };
}

/* ════════════════════════════════════════════════════════════════
   PROCESS METADATA (v2 — includes fx block + transitions)
   ════════════════════════════════════════════════════════════════ */

export function buildProcessMetadata({ videoFileName, duration, overlays, fx }) {
  const dur = Number.isFinite(duration) && duration > 0 ? duration : Number.MAX_SAFE_INTEGER;
  const gfx = { ...defaultGlobalFx(), ...(fx || {}) };

  const overlayData = (overlays || [])
    .filter((o) => !!o?.url?.trim())
    .map((o) => {
      const start = clamp(o.start, 0, dur);
      const end = clamp(o.end, 0, dur);
      if (end <= start) return null;
      const tin = normalizeTransition(o.transIn, o.type);
      const tout = normalizeTransition(o.transOut, o.type);
      return {
        url: String(o.url).trim(),
        start: +start.toFixed(3),
        end: +end.toFixed(3),
        type: o.type === "video" ? "video" : "image",
        fit: o.fit === "cover" ? "cover" : "contain",
        focus: {
          x: clamp(o.focus?.x ?? 0.5, 0, 1),
          y: clamp(o.focus?.y ?? 0.5, 0, 1),
        },
        bg:
          o.bg && o.bg.type && o.bg.type !== "none" && o.type !== "video" && o.fit !== "cover"
            ? {
                type: o.bg.type,
                blur: clamp(o.bg.blur ?? 20, 1, 100),
                color: o.bg.color || "#000000",
                color2: o.bg.color2 || "#7c3aed",
                angle: clamp(o.bg.angle ?? 180, 0, 360),
              }
            : null,
        opacity: clamp(o.opacity ?? 1, 0, 1),
        scale: o.scale != null && o.scale !== "" ? clamp(o.scale, 0.05, 4) : null,
        transIn: tin.type === "none" ? null : tin,
        transOut: tout.type === "none" ? null : tout,
        kenBurns: o.ken?.enabled
          ? {
              enabled: true,
              mode: o.ken.mode || "auto",
              strength: clamp(o.ken.strength ?? 0.2, 0, 0.5),
              speed: Math.max(0.5, o.ken.speed ?? 8),
            }
          : null,
      };
    })
    .filter(Boolean);

  const v = clamp(gfx.vignette, 0, 100) / 100;
  const n = clamp(gfx.noise, 0, 100) / 100;

  return {
    videoFilename: videoFileName,
    overlays: overlayData,

    // ── v2 normalized FX block (backend: parse_global_fx) ──
    fx: {
      brightness: clamp(gfx.brightness, -100, 100) / 100,
      contrast: clamp(gfx.contrast, -100, 100) / 100,
      saturation: clamp(gfx.saturation, -100, 100) / 100,
      hue: clamp(gfx.hue, -180, 180),
      blur: clamp(gfx.blur, 0, 50),
      grayscale: clamp(gfx.grayscale, 0, 100) / 100,
      sepia: clamp(gfx.sepia, 0, 100) / 100,
      fadeIn: clamp(gfx.fadeIn, 0, 10),
      fadeOut: clamp(gfx.fadeOut, 0, 10),
      vignette: v,
      noise: n,
    },

    // ── legacy top-level fields (kept for backward compat) ──
    vignette: {
      enabled: v > 0,
      strength: v,
      centerX: 0.5,
      centerY: 0.5,
      innerRadius: 0.4,
      outerRadius: 1.0,
      color: [0, 0, 0],
      maxAlpha: 0.99 * v,
    },
    noise: n,
  };
}